import { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { searchLocations, searchKoreanLocations, searchOverseasLocations, OVERSEAS_LOCATIONS } from '@/mocks/locationData';
import type { LocationData as MockLocationData } from '@/mocks/locationData';

export type LocationData = MockLocationData;

interface BirthLocationInputProps {
  onNext: (location: LocationData) => void;
  onBack: () => void;
}

function getKakao(): any {
  return (window as any).kakao;
}

async function kakaoSearch(query: string): Promise<LocationData[] | null> {
  return new Promise((resolve) => {
    const kakao = getKakao();
    if (!kakao) { resolve(null); return; }

    const doSearch = () => {
      try {
        const ps = new kakao.maps.services.Places();
        ps.keywordSearch(query, (data: any[], status: string) => {
          if (status === kakao.maps.services.Status.OK) {
            resolve(data.slice(0, 8).map((p: any) => ({
              id: p.id,
              name: p.place_name,
              address: p.road_address_name || p.address_name,
              longitude: parseFloat(p.x),
              latitude: parseFloat(p.y),
            })));
          } else {
            resolve([]);
          }
        });
      } catch {
        resolve(null);
      }
    };

    if (kakao.maps?.services) {
      doSearch();
    } else if (kakao.maps?.load) {
      kakao.maps.load(() => doSearch());
    } else {
      resolve(null);
    }
    setTimeout(() => resolve(null), 3000);
  });
}

const POPULAR_KOREAN: LocationData[] = [
  { id: 'seoul', name: '서울특별시', address: '서울특별시', longitude: 126.978, latitude: 37.566 },
  { id: 'busan', name: '부산광역시', address: '부산광역시', longitude: 129.075, latitude: 35.180 },
  { id: 'daegu', name: '대구광역시', address: '대구광역시', longitude: 128.601, latitude: 35.870 },
  { id: 'incheon', name: '인천광역시', address: '인천광역시', longitude: 126.705, latitude: 37.456 },
  { id: 'gwangju', name: '광주광역시', address: '광주광역시', longitude: 126.852, latitude: 35.160 },
  { id: 'daejeon', name: '대전광역시', address: '대전광역시', longitude: 127.385, latitude: 36.351 },
  { id: 'jeju', name: '제주시', address: '제주특별자치도 제주시', longitude: 126.531, latitude: 33.489 },
  { id: 'ulsan', name: '울산광역시', address: '울산광역시', longitude: 129.315, latitude: 35.538 },
];

const POPULAR_OVERSEAS: LocationData[] = OVERSEAS_LOCATIONS.filter(l =>
  ['tokyo', 'osaka', 'beijing', 'shanghai', 'hongkong', 'taipei', 'singapore', 'new-york', 'los-angeles', 'london', 'paris', 'sydney', 'bangkok', 'dubai'].includes(l.id)
);

const COUNTRY_FLAGS: Record<string, string> = {
  '일본': '🇯🇵', '중국': '🇨🇳', '홍콩': '🇭🇰', '대만': '🇹🇼',
  '싱가포르': '🇸🇬', '태국': '🇹🇭', '베트남': '🇻🇳', '필리핀': '🇵🇭',
  '인도네시아': '🇮🇩', '말레이시아': '🇲🇾', '미국': '🇺🇸', '캐나다': '🇨🇦',
  '영국': '🇬🇧', '프랑스': '🇫🇷', '독일': '🇩🇪', '네덜란드': '🇳🇱',
  '스위스': '🇨🇭', '이탈리아': '🇮🇹', '스페인': '🇪🇸', '스웨덴': '🇸🇪',
  '아랍에미리트': '🇦🇪', '호주': '🇦🇺', '뉴질랜드': '🇳🇿',
  '인도': '🇮🇳',
};

type Tab = 'korea' | 'overseas';

export default function BirthLocationInput({ onNext, onBack }: BirthLocationInputProps) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>('korea');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<LocationData[]>([]);
  const [selected, setSelected] = useState<LocationData | null>(null);
  const [loading, setLoading] = useState(false);
  const [usingFallback, setUsingFallback] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const kakao = getKakao();
    if (kakao?.maps?.load && !kakao.maps.services) {
      kakao.maps.load(() => {});
    }
  }, []);

  const handleSearch = useCallback((value: string, currentTab: Tab) => {
    setQuery(value);
    setSelected(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!value.trim()) {
      setResults([]);
      setUsingFallback(false);
      return;
    }

    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      if (currentTab === 'overseas') {
        setResults(searchOverseasLocations(value));
        setUsingFallback(false);
        setLoading(false);
        return;
      }

      const kakaoResult = await kakaoSearch(value);
      if (kakaoResult !== null && kakaoResult.length > 0) {
        setResults(kakaoResult);
        setUsingFallback(false);
      } else if (kakaoResult !== null) {
        const fallback = searchLocations(value);
        setResults(fallback);
        setUsingFallback(fallback.length > 0);
      } else {
        setResults(searchKoreanLocations(value));
        setUsingFallback(true);
      }
      setLoading(false);
    }, 350);
  }, []);

  const handleTabChange = (newTab: Tab) => {
    setTab(newTab);
    setQuery('');
    setResults([]);
    setSelected(null);
    setUsingFallback(false);
  };

  const handleSelect = (loc: LocationData) => {
    setSelected(loc);
    setQuery(loc.name);
    setResults([]);
    setUsingFallback(false);
  };

  const handleClear = () => {
    setSelected(null);
    setQuery('');
    setResults([]);
    setUsingFallback(false);
  };

  const popularCities = tab === 'korea' ? POPULAR_KOREAN : POPULAR_OVERSEAS;

  return (
    <div className="min-h-dvh flex flex-col bg-white" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
      {/* 헤더 고정 영역 */}
      <div className="px-5 pt-5 pb-0 flex-shrink-0">
        <div className="flex items-center mb-4">
          <button
            onClick={onBack}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer mr-2"
          >
            <i className="ri-arrow-left-line text-gray-700" />
          </button>
          <img
            src="https://public.readdy.ai/ai/img_res/ff2a104a-70f3-4324-bb8d-41c2b9fa3434.png"
            alt="바른사주"
            className="h-7 object-contain"
          />
        </div>
        <div className="flex gap-1.5">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className={`h-1 flex-1 rounded-full ${i <= 2 ? 'bg-gray-900' : 'bg-gray-200'}`} />
          ))}
        </div>
      </div>

      {/* 스크롤 가능한 콘텐츠 영역 */}
      <div className="flex-1 overflow-y-auto px-5 pt-5 pb-4">
        <div className="max-w-sm mx-auto w-full">
          <p className="text-gray-400 text-sm mb-1.5">{t('location.step')}</p>
          <h2 className="text-2xl font-bold text-gray-900 mb-3 leading-snug">
            {t('location.title').split('\n').map((line, i) => (
              <span key={i}>{line}{i < t('location.title').split('\n').length - 1 && <br />}</span>
            ))}
          </h2>

          <div className="flex items-start gap-2 bg-amber-50 rounded-xl p-3 mb-4 border border-amber-100">
            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-map-pin-line text-amber-500 text-sm" />
            </div>
            <p className="text-amber-700 text-xs leading-relaxed">{t('location.hint')}</p>
          </div>

          {/* 국내 / 해외 탭 */}
          <div className="flex bg-gray-100 rounded-full p-1 mb-3">
            <button
              onClick={() => handleTabChange('korea')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-full text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${
                tab === 'korea' ? 'bg-white text-gray-900' : 'text-gray-400'
              }`}
            >
              <span>🇰🇷</span> {t('location.tabKorea')}
            </button>
            <button
              onClick={() => handleTabChange('overseas')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-full text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${
                tab === 'overseas' ? 'bg-white text-gray-900' : 'text-gray-400'
              }`}
            >
              <span>🌏</span> {t('location.tabOverseas')}
            </button>
          </div>

          {/* 검색창 */}
          <div className="relative mb-2">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
              {loading
                ? <div className="w-3 h-3 rounded-full border-2 border-gray-400 border-t-transparent animate-spin" />
                : <i className="ri-search-line text-gray-400 text-sm" />}
            </div>
            <input
              type="text"
              value={query}
              onChange={(e) => handleSearch(e.target.value, tab)}
              placeholder={tab === 'korea' ? t('location.placeholderKorea') : t('location.placeholderOverseas')}
              className="w-full pl-11 pr-10 py-3.5 rounded-2xl bg-gray-50 border border-gray-200 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-gray-400 focus:bg-white transition-all"
              autoComplete="off"
            />
            {query && (
              <button
                onClick={handleClear}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center cursor-pointer"
              >
                <i className="ri-close-circle-fill text-gray-300 text-lg" />
              </button>
            )}
          </div>

          {/* 영어 검색 힌트 (해외 탭) */}
          {tab === 'overseas' && !query && !selected && (
            <p className="text-xs text-gray-400 px-1 mb-2.5">
              <i className="ri-information-line mr-1" />
              {t('location.overseas.hint')}
            </p>
          )}

          {/* 폴백 안내 */}
          {usingFallback && (
            <div className="flex items-center gap-2 mb-2 px-1">
              <i className="ri-wifi-off-line text-gray-300 text-xs" />
              <p className="text-xs text-gray-300">{t('location.fallback')}</p>
            </div>
          )}

          {/* 검색 결과 */}
          {results.length > 0 && !selected && (
            <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden mb-3">
              {results.map((loc, idx) => (
                <button
                  key={loc.id}
                  onClick={() => handleSelect(loc)}
                  className={`w-full flex items-start gap-3 px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer text-left ${
                    idx < results.length - 1 ? 'border-b border-gray-50' : ''
                  }`}
                >
                  <div className="w-7 h-7 flex items-center justify-center bg-gray-100 rounded-xl flex-shrink-0 mt-0.5 text-sm">
                    {loc.isOverseas && loc.country
                      ? <span>{COUNTRY_FLAGS[loc.country] ?? '🌍'}</span>
                      : <i className="ri-map-pin-2-line text-gray-500 text-xs" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-gray-900 truncate">{loc.name}</p>
                      {loc.englishName && (
                        <p className="text-xs text-gray-400 truncate">{loc.englishName}</p>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5 truncate">{loc.address}</p>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* 검색 결과 없음 */}
          {!loading && query.trim() && results.length === 0 && !selected && (
            <div className="text-center py-5 mb-3">
              <div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-2xl mx-auto mb-2">
                <i className="ri-map-pin-line text-gray-400" />
              </div>
              <p className="text-gray-400 text-sm">{t('noResults')}</p>
              <p className="text-gray-300 text-xs mt-1">{t('noResultsHint')}</p>
            </div>
          )}

          {/* 검색어 없을 때 인기 도시 */}
          {!query && !selected && (
            <div className="mb-3">
              <p className="text-xs text-gray-400 mb-2.5 px-1">
                {tab === 'korea' ? t('location.popular.korea') : t('location.popular.overseas')}
              </p>
              <div className="grid grid-cols-2 gap-2">
                {popularCities.map((city) => (
                  <button
                    key={city.id}
                    onClick={() => handleSelect(city)}
                    className="flex items-center gap-2 px-3 py-2.5 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors cursor-pointer text-left"
                  >
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-sm">
                      {city.isOverseas && city.country
                        ? <span>{COUNTRY_FLAGS[city.country] ?? '🌍'}</span>
                        : <i className="ri-map-pin-line text-gray-400 text-xs" />}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-700 truncate">{city.name}</p>
                      {city.englishName && (
                        <p className="text-xs text-gray-400 truncate">{city.englishName}</p>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* 선택된 장소 */}
          {selected && (
            <div className="bg-gray-50 rounded-2xl p-3.5 mb-3 border-2 border-gray-900">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center bg-gray-900 rounded-xl flex-shrink-0 text-xl">
                  {selected.isOverseas && selected.country
                    ? <span>{COUNTRY_FLAGS[selected.country] ?? '🌍'}</span>
                    : <i className="ri-map-pin-2-line text-white text-sm" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-bold text-gray-900 text-sm truncate">{selected.name}</p>
                    {selected.englishName && (
                      <p className="text-xs text-gray-400">{selected.englishName}</p>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 truncate">{selected.address}</p>
                </div>
                <button onClick={handleClear} className="w-6 h-6 flex items-center justify-center cursor-pointer flex-shrink-0">
                  <i className="ri-close-line text-gray-400" />
                </button>
              </div>
              <div className="mt-3 pt-3 border-t border-gray-200 flex gap-4">
                <div className="text-center flex-1">
                  <p className="text-xs text-gray-400 mb-0.5">{t('location.longitude')}</p>
                  <p className="text-sm font-bold text-gray-900">{selected.longitude.toFixed(4)}°</p>
                </div>
                <div className="w-px bg-gray-200" />
                <div className="text-center flex-1">
                  <p className="text-xs text-gray-400 mb-0.5">{t('location.latitude')}</p>
                  <p className="text-sm font-bold text-gray-900">{selected.latitude.toFixed(4)}°</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 고정 하단 버튼 */}
      <div className="flex-shrink-0 px-5 pt-3 bg-white border-t border-gray-100" style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 1.25rem)' }}>
        <div className="max-w-sm mx-auto w-full">
          <button
            onClick={() => selected && onNext(selected)}
            disabled={!selected}
            className={`w-full font-bold text-base py-4 rounded-2xl flex items-center justify-center gap-2 transition-all whitespace-nowrap ${
              selected
                ? 'bg-gray-900 text-white hover:bg-gray-800 active:scale-95 cursor-pointer'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            {t('next')} <i className="ri-arrow-right-line text-sm" />
          </button>
        </div>
      </div>
    </div>
  );
}
